import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MemberVerification = ({ member, onClear }) => {
  if (!member) {
    return (
      <div className="bg-card rounded-lg p-6 border border-border">
        <div className="text-center py-12">
          <Icon name="UserCheck" size={64} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No Member Scanned</h3>
          <p className="text-muted-foreground">
            Scan a member's barcode to view their details and check-in status
          </p>
        </div>
      </div>
    );
  }

  const memberProfile = member?.user_profiles;
  const membershipInfo = member?.tenant_members?.[0];
  const isActive = membershipInfo?.is_active;

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Member Verification</h2>
        <Button
          onClick={onClear}
          variant="ghost"
          size="sm"
          iconName="X"
        >
          Clear
        </Button>
      </div>
      {/* Member Profile */}
      <div className="flex items-start gap-4 mb-6">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
          {memberProfile?.avatar_url ? (
            <img
              src={memberProfile?.avatar_url}
              alt={memberProfile?.full_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Icon name="User" size={32} className="text-primary" />
          )}
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-foreground mb-1">
            {memberProfile?.full_name}
          </h3>
          <p className="text-sm text-muted-foreground mb-2">
            {memberProfile?.email}
          </p>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                isActive
                  ? 'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
              }`}
            >
              <Icon name={isActive ? 'CheckCircle' : 'XCircle'} size={14} />
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>
      {/* Membership Details */}
      <div className="space-y-4">
        <div className="p-4 bg-muted rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Role</p>
              <p className="font-semibold capitalize">
                {membershipInfo?.role?.replace('_', ' ')}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Membership Plan</p>
              <p className="font-semibold capitalize">
                {membershipInfo?.membership_plan || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Barcode Info */}
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Barcode ID</p>
          <p className="font-mono text-sm break-all">
            {member?.barcode_data}
          </p>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Type</p>
              <p className="text-sm font-medium capitalize">
                {member?.barcode_type?.replace('_', ' ')}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Scan Count</p>
              <p className="text-sm font-medium">
                {member?.scan_count || 0} times
              </p>
            </div>
          </div>
        </div>

        {/* Last Scanned */}
        {member?.last_scanned_at && (
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Last Scanned</p>
            <p className="text-sm font-medium">
              {new Date(member?.last_scanned_at)?.toLocaleString()}
            </p>
          </div>
        )}

        {/* Access Status */}
        <div
          className={`p-4 rounded-lg border-2 ${
            isActive
              ? 'bg-success/10 border-success' :'bg-destructive/10 border-destructive'
          }`}
        >
          <div className="flex items-center gap-3">
            <Icon
              name={isActive ? 'CheckCircle' : 'XCircle'}
              size={24}
              className={isActive ? 'text-success' : 'text-destructive'}
            />
            <div>
              <p className="font-semibold">
                {isActive ? 'Access Granted' : 'Access Denied'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isActive
                  ? 'Member can check in to the gym' :'Membership is inactive or suspended'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberVerification;